import { useEffect, useRef, useState, type ReactNode } from "react";
import type { EntityRecord } from "@wedding/schemas";
import { Button } from "@wedding/ui";
import { CalendarDays, Store, UserRound, X } from "lucide-react";

const statuses = ["未开始", "进行中", "待确认", "已完成", "延期", "已取消"];
const priorities = ["高", "中", "低"];

type BulkField = "status" | "priority" | "dueDate" | "owner" | "vendorId";

export function TaskBulkEditor({
  open,
  count,
  saving,
  vendors,
  onClose,
  onSave,
}: {
  open: boolean;
  count: number;
  saving: boolean;
  vendors: EntityRecord[];
  onClose(): void;
  onSave(patch: Record<string, unknown>): void;
}) {
  const [enabled, setEnabled] = useState<Set<BulkField>>(new Set());
  const [values, setValues] = useState<Record<BulkField, string>>({
    status: "进行中",
    priority: "中",
    dueDate: "",
    owner: "",
    vendorId: "",
  });
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    setEnabled(new Set());
    const previousFocus = document.activeElement as HTMLElement | null;
    const timer = window.setTimeout(() =>
      dialogRef.current
        ?.querySelector<HTMLElement>("input, select, button")
        ?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [onClose, open, saving]);

  if (!open) return null;

  const toggle = (field: BulkField, checked: boolean) => {
    setEnabled((current) => {
      const next = new Set(current);
      if (checked) next.add(field);
      else next.delete(field);
      return next;
    });
  };
  const submit = () => {
    const patch = Object.fromEntries(
      [...enabled].map((field) => [field, values[field]]),
    );
    onSave(patch);
  };

  const field = (
    key: BulkField,
    label: string,
    icon: ReactNode,
    control: ReactNode,
  ) => (
    <div className={`bulk-edit-field ${enabled.has(key) ? "enabled" : ""}`}>
      <label className="bulk-edit-toggle">
        <input
          type="checkbox"
          checked={enabled.has(key)}
          onChange={(event) => toggle(key, event.target.checked)}
        />
        {icon}
        <span>修改{label}</span>
      </label>
      <div className="bulk-edit-control">{control}</div>
    </div>
  );

  return (
    <div className="drawer-backdrop">
      <aside
        ref={dialogRef}
        className="drawer task-bulk-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-task-title"
      >
        <header>
          <div>
            <span className="eyebrow">批量编辑</span>
            <h2 id="bulk-task-title">统一修改 {count} 项任务</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>
        <div className="bulk-edit-fields">
          <p className="bulk-edit-help">
            只会修改勾选的字段，其他内容保持不变。
          </p>
          {field(
            "status",
            "状态",
            null,
            <select
              aria-label="统一状态"
              value={values.status}
              disabled={!enabled.has("status")}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            >
              {statuses.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>,
          )}
          {field(
            "priority",
            "优先级",
            null,
            <select
              aria-label="统一优先级"
              value={values.priority}
              disabled={!enabled.has("priority")}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  priority: event.target.value,
                }))
              }
            >
              {priorities.map((priority) => (
                <option key={priority}>{priority}</option>
              ))}
            </select>,
          )}
          {field(
            "dueDate",
            "截止日期",
            <CalendarDays size={16} />,
            <input
              aria-label="统一截止日期"
              type="date"
              value={values.dueDate}
              disabled={!enabled.has("dueDate")}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  dueDate: event.target.value,
                }))
              }
            />,
          )}
          {field(
            "owner",
            "负责人",
            <UserRound size={16} />,
            <input
              aria-label="统一负责人"
              value={values.owner}
              disabled={!enabled.has("owner")}
              placeholder="留空可清除负责人"
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  owner: event.target.value,
                }))
              }
            />,
          )}
          {field(
            "vendorId",
            "关联供应商",
            <Store size={16} />,
            <select
              aria-label="统一关联供应商"
              value={values.vendorId}
              disabled={!enabled.has("vendorId")}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  vendorId: event.target.value,
                }))
              }
            >
              <option value="">清除供应商关联</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {String(vendor.data.vendorName ?? "未命名供应商")}
                  {vendor.data.vendorType
                    ? ` · ${String(vendor.data.vendorType)}`
                    : ""}
                </option>
              ))}
            </select>,
          )}
        </div>
        <footer>
          <Button className="secondary" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button onClick={submit} disabled={saving || enabled.size === 0}>
            {saving ? "正在更新…" : `更新 ${count} 项任务`}
          </Button>
        </footer>
      </aside>
    </div>
  );
}
